import { useState, Fragment } from "react";

// Actor-coded flowchart palette (kept distinct from portal navy theme so
// each box reads clearly): teal / steel / green / purple.
const ACTORS = {
  you: { color: "#0E8C7F", label: "You" },
  auto: { color: "#4A6D8C", label: "Automatic" },
  result: { color: "#27AE60", label: "Result" },
  ai: { color: "#7D3C98", label: "AI" },
};

const FLOWCHARTS = [
  {
    slug: "finding-information",
    title: "Finding Information",
    subtitle: "Ask anything. The system searches everything we've built.",
    steps: [
      { actor: "you", text: "Leanne asks a question" },
      { actor: "ai", text: "Opens a Perplexity Space — Nairana, Defender, Travel, Health, Wealth, or DLS Guide" },
      { actor: "auto", text: "Space searches SharePoint files" },
      { actor: "result", text: "Clear answer with the source" },
    ],
  },
  {
    slug: "capture-and-file",
    title: "Capture and File",
    subtitle: "Scan it or email it. It files itself.",
    steps: [
      { actor: "you", text: "Leanne scans or emails a document" },
      { actor: "auto", text: "Email arrives at wc@ / hl@ / fl@" },
      { actor: "auto", text: "Power Automate reads the subject, extracts the attachment, routes to the right SharePoint folder" },
      { actor: "result", text: "Filed with metadata · To Do task created for CD review" },
    ],
  },
  {
    slug: "automation",
    title: "Automation — What Runs Without You",
    subtitle: "The system watches. You only act when it tells you to.",
    steps: [
      { actor: "you", text: "No action needed", passive: true },
      { actor: "auto", text: "Power Automate monitors: renewal alerts 30 days out, hourly register exports, file review tasks, Xero / CAS360 To Do sync" },
      { actor: "result", text: "Alerts arrive in To Do or email when something needs attention" },
    ],
  },
  {
    slug: "prompts",
    title: "Prompts and AI Work",
    subtitle: "Describe what you want. AI builds it. You review.",
    steps: [
      { actor: "you", text: "CD describes an outcome in plain English" },
      { actor: "ai", text: "Claude or Perplexity builds the document, spreadsheet, or analysis" },
      { actor: "you", text: "CD reviews the result" },
      { actor: "result", text: "Final version filed to SharePoint" },
    ],
  },
  {
    slug: "macquarie-banking",
    title: "Macquarie Banking",
    subtitle: "Bank statements arrive and file themselves.",
    steps: [
      { actor: "auto", text: "Macquarie sends scheduled statement emails" },
      { actor: "auto", text: "Email forwarded to wc@sandeu.au" },
      { actor: "auto", text: "Power Automate extracts the PDF attachment" },
      { actor: "result", text: "Filed to WC/BANK/{Entity} with the correct filename" },
    ],
  },
  {
    slug: "julie-tim-quarterly",
    title: "Julie and Tim — Quarterly Advisor Cycle",
    subtitle: "Every quarter, the advisor pack builds itself. You just approve.",
    steps: [
      { actor: "auto", text: "Quarter ends" },
      { actor: "auto", text: "Cowork pulls WC/TAX documents and builds the summary spreadsheet" },
      { actor: "ai", text: "Claude drafts the covering email with attachments" },
      { actor: "you", text: "CD reviews and approves the send" },
      { actor: "result", text: "Email sent to Julie / Tim with the full quarterly pack" },
    ],
  },
];

function FlowBox({ step }) {
  const actor = ACTORS[step.actor];
  return (
    <div
      className={`bg-white rounded-2xl border-y border-r border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4 flex-1 min-w-0 ${
        step.passive ? "opacity-60" : ""
      }`}
      style={{ borderLeft: `4px solid ${actor.color}` }}
    >
      <span
        className="inline-block text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded text-white"
        style={{ backgroundColor: actor.color }}
      >
        {actor.label}
      </span>
      <p className="mt-2 text-sm text-slate-900 leading-snug">{step.text}</p>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex items-center justify-center text-slate-400 shrink-0">
      <span className="sm:hidden text-2xl leading-none">↓</span>
      <span className="hidden sm:inline text-2xl leading-none px-1">→</span>
    </div>
  );
}

function Flow({ steps }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-1 sm:items-stretch">
      {steps.map((step, i) => (
        <Fragment key={i}>
          <FlowBox step={step} />
          {i < steps.length - 1 && <Arrow />}
        </Fragment>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {Object.entries(ACTORS).map(([key, { color, label }]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700"
        >
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
          {key === "you" ? "You do this" : label}
        </span>
      ))}
    </div>
  );
}

function CardPreview({ steps }) {
  return (
    <div className="flex items-center gap-1 mt-3">
      {steps.map((step, i) => (
        <Fragment key={i}>
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: ACTORS[step.actor].color }}
          />
          {i < steps.length - 1 && (
            <span className="w-2 h-px bg-slate-300 shrink-0" />
          )}
        </Fragment>
      ))}
    </div>
  );
}

function FlowchartCard({ flowchart, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 text-left hover:shadow-md hover:border-[#4A6D8C]/40 transition-all w-full"
    >
      <h3 className="font-semibold text-slate-900 text-base">
        {flowchart.title}
      </h3>
      <p className="text-sm text-slate-500 mt-1">{flowchart.subtitle}</p>
      <CardPreview steps={flowchart.steps} />
      <span className="inline-block mt-3 text-xs font-medium text-[#1B3A5C]">
        Tap to expand &rarr;
      </span>
    </button>
  );
}

function FlowchartDetail({ flowchart, onBack }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
        >
          &larr; Back
        </button>
        <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
          {flowchart.title}
        </h2>
      </div>
      <p className="text-sm text-slate-500 mb-6 px-2">{flowchart.subtitle}</p>
      <Legend />
      <Flow steps={flowchart.steps} />
    </div>
  );
}

export default function HowItWorksView() {
  const [activeIndex, setActiveIndex] = useState(null);

  if (activeIndex != null) {
    return (
      <FlowchartDetail
        flowchart={FLOWCHARTS[activeIndex]}
        onBack={() => setActiveIndex(null)}
      />
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900 tracking-tight mb-2 px-1">
        How It Works
      </h2>
      <p className="text-sm text-slate-500 mb-6 px-1">
        Six flowcharts showing how the Sandeu Digital Life System runs.
      </p>

      <Legend />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FLOWCHARTS.map((f, i) => (
          <FlowchartCard
            key={f.slug}
            flowchart={f}
            onOpen={() => setActiveIndex(i)}
          />
        ))}
      </div>
    </div>
  );
}
